import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../api";

const toLocalYYYYMMDD = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeLogoUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  return url.trim();
};

const getInitials = (name = "") => {
  const clean = String(name).trim();
  if (!clean) return "M";
  return clean.charAt(0).toUpperCase();
};

const normalizeCity = (value = "") => String(value || "").trim();

/* ─── inject global styles once ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --red:       #E8192C;
    --red-dark:  #B8121F;
    --red-glow:  rgba(232,25,44,0.18);
    --charcoal:  #1C1C1E;
    --surface:   #242426;
    --surface2:  #2E2E30;
    --border:    rgba(255,255,255,0.08);
    --muted:     rgba(255,255,255,0.48);
    --font-cond: 'Barlow Condensed', sans-serif;
    --font-body: 'Barlow', sans-serif;
  }

  .maco-root { font-family: var(--font-body); background: var(--charcoal); color: #fff; min-height: 100vh; overflow-x: hidden; }

  .maco-brand-strip {
    position: relative; z-index: 10;
    display: flex; align-items: center; justify-content: space-between; gap: 18px;
    padding: 22px 2rem; max-width: 1280px; margin: 0 auto;
  }
  .maco-brand-left { display: flex; align-items: center; gap: 14px; min-width: 0; }
  .maco-nav-logo-wrap {
    width: 54px; height: 54px; border-radius: 14px;
    background: #fff; display: flex; align-items: center; justify-content: center;
    overflow: hidden; flex-shrink: 0; box-shadow: 0 14px 34px rgba(0,0,0,0.22);
  }
  .maco-nav-logo-wrap img { width: 46px; height: 46px; object-fit: contain; }
  .maco-nav-logo-fallback {
    width: 54px; height: 54px; border-radius: 14px;
    background: var(--red); display: flex; align-items: center; justify-content: center;
    font-family: var(--font-cond); font-size: 26px; font-weight: 900; color: #fff; flex-shrink: 0;
    box-shadow: 0 14px 34px rgba(232,25,44,0.28);
  }
  .maco-nav-name { font-family: var(--font-cond); font-size: 26px; font-weight: 900; letter-spacing: 0.04em; color: #fff; line-height: 1; }
  .maco-nav-sub { font-size: 11px; color: var(--muted); letter-spacing: 0.08em; text-transform: uppercase; margin-top: 5px; }
  .maco-strip-actions { display: flex; align-items: center; gap: 10px; }
  .maco-strip-link {
    padding: 9px 14px; font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.6); text-decoration: none; border-radius: 10px;
  }
  .maco-strip-link:hover { color: #fff; background: rgba(255,255,255,0.06); }
  .maco-strip-cta {
    padding: 10px 22px; font-family: var(--font-cond); font-size: 15px; font-weight: 900;
    letter-spacing: 0.08em; text-transform: uppercase; color: #fff; text-decoration: none;
    background: var(--red); border-radius: 10px; transition: background .2s, transform .15s;
  }
  .maco-strip-cta:hover { background: var(--red-dark); transform: translateY(-1px); }

  .maco-page-bg {
    position: relative; min-height: 100vh;
    background:
      radial-gradient(ellipse 720px 520px at 78% 25%, var(--red-glow), transparent 62%),
      radial-gradient(ellipse 520px 420px at 10% 82%, rgba(232,25,44,0.08), transparent 62%),
      linear-gradient(180deg, #1C1C1E 0%, #18181A 100%);
  }
  .maco-page-bg::before {
    content: ""; position: absolute; inset: 0; pointer-events: none;
    background-image: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
    background-size: 72px 72px; mask-image: linear-gradient(to bottom, black, transparent 85%);
  }

  .maco-hero {
    position: relative; z-index: 1; min-height: calc(100vh - 98px);
    display: grid; grid-template-columns: minmax(0, 1fr) minmax(380px, 470px); gap: 58px; align-items: center;
    padding: 36px 2rem 70px; max-width: 1280px; margin: 0 auto;
  }
  @media (max-width: 980px) {
    .maco-hero { grid-template-columns: 1fr; padding: 26px 1.25rem 54px; gap: 28px; }
    .maco-hero-search { order: 2; }
    .maco-strip-actions { display: none; }
  }

  .maco-hero-content { position: relative; z-index: 1; }
  .maco-hero-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: var(--font-cond); font-size: 12px; font-weight: 800; letter-spacing: 0.16em;
    text-transform: uppercase; color: var(--red);
    border: 1px solid rgba(232,25,44,0.35); border-radius: 6px;
    padding: 6px 12px; margin-bottom: 20px; background: rgba(232,25,44,0.06);
  }
  .maco-hero-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--red); animation: pulse 2s ease-in-out infinite; }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.3; } }

  .maco-hero-title {
    font-family: var(--font-cond); font-size: clamp(50px, 7vw, 88px);
    font-weight: 900; line-height: 0.92; letter-spacing: -0.01em;
    color: #fff; margin-bottom: 22px;
  }
  .maco-hero-title-red { color: var(--red); }
  .maco-hero-desc { font-size: 16px; line-height: 1.7; color: rgba(255,255,255,0.58); max-width: 560px; margin-bottom: 26px; }

  .maco-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; max-width: 420px; margin-bottom: 22px; }
  .maco-stat {
    background: rgba(255,255,255,0.045); border: 1px solid var(--border); border-radius: 14px;
    padding: 16px 14px; backdrop-filter: blur(12px);
  }
  .maco-stat-num { font-family: var(--font-cond); font-size: 30px; font-weight: 900; color: #fff; line-height: 1; }
  .maco-stat-label { font-size: 11px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 0.06em; margin-top: 4px; }

  .maco-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 26px; }
  .maco-pill {
    font-size: 12px; font-weight: 700; letter-spacing: 0.04em; color: rgba(255,255,255,0.62);
    background: rgba(255,255,255,0.05); border: 1px solid var(--border);
    border-radius: 100px; padding: 7px 14px;
  }

  .maco-quick-routes { max-width: 640px; margin-top: 4px; }
  .maco-quick-routes-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
  .maco-quick-routes-title h3 { font-family: var(--font-cond); font-size: 24px; font-weight: 900; color: #fff; letter-spacing: 0.02em; }
  .maco-quick-routes-title span { color: var(--muted); font-size: 12px; font-weight: 700; }
  .maco-quick-grid { display: grid; gap: 10px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 620px) { .maco-quick-grid { grid-template-columns: 1fr; } }
  .maco-route-chip {
    width: 100%; text-align: left; cursor: pointer; border: 1px solid rgba(255,255,255,0.09); border-radius: 16px;
    background: rgba(255,255,255,0.055); padding: 15px; color: #fff; transition: transform .18s, background .18s, border-color .18s;
  }
  .maco-route-chip:hover { transform: translateY(-2px); background: rgba(255,255,255,0.09); border-color: rgba(232,25,44,0.65); }
  .maco-route-main { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .maco-route-name { font-family: var(--font-cond); font-size: 21px; font-weight: 900; line-height: 1; }
  .maco-route-arrow { color: var(--red); padding: 0 4px; }
  .maco-route-book { background: var(--red); color: white; border-radius: 999px; padding: 7px 11px; font-family: var(--font-cond); font-size: 12px; font-weight: 900; letter-spacing: 0.07em; white-space: nowrap; }
  .maco-route-meta { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; color: rgba(255,255,255,0.48); font-size: 12px; font-weight: 700; }
  .maco-route-price { color: #fff; }

  .maco-hero-search { position: relative; z-index: 1; display: flex; justify-content: center; align-items: center; }
  .maco-search-card {
    width: 100%; max-width: 455px;
    background: #fff; border-radius: 24px; padding: 28px;
    box-shadow: 0 34px 90px rgba(0,0,0,0.62), 0 0 0 1px rgba(255,255,255,0.08);
    color: #1C1C1E;
  }
  .maco-search-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 22px; }
  .maco-search-badge {
    display: inline-block; font-family: var(--font-cond); font-size: 11px; font-weight: 900;
    letter-spacing: 0.12em; text-transform: uppercase; color: #fff; background: var(--red);
    border-radius: 5px; padding: 5px 10px; margin-bottom: 9px;
  }
  .maco-search-title { font-family: var(--font-cond); font-size: 31px; font-weight: 900; line-height: .95; color: #1C1C1E; }
  .maco-search-sub { font-size: 13px; color: #888; margin-top: 6px; }
  .maco-search-logo { width: 60px; height: 60px; border-radius: 14px; object-fit: contain; background: #f4f4f4; padding: 6px; flex-shrink: 0; }

  .maco-field { margin-bottom: 14px; }
  .maco-label { display: block; font-size: 10px; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; color: #999; margin-bottom: 7px; }
  .maco-input {
    width: 100%; padding: 13px 16px; border-radius: 12px; border: 1.5px solid #E8E8E8;
    background: #F7F7F7; font-family: var(--font-body); font-size: 15px; font-weight: 700;
    color: #1C1C1E; outline: none; transition: border-color .2s, background .2s; appearance: none;
  }
  select.maco-input {
    background-image: linear-gradient(45deg, transparent 50%, #999 50%), linear-gradient(135deg, #999 50%, transparent 50%);
    background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%;
    background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; padding-right: 36px;
  }
  .maco-input:focus { border-color: var(--red); background-color: #fff; }
  .maco-input::placeholder { color: #bbb; font-weight: 500; }

  .maco-swap-row { display: grid; grid-template-columns: 1fr 42px 1fr; gap: 9px; align-items: end; margin-bottom: 14px; }
  .maco-swap-btn {
    width: 42px; height: 48px; border-radius: 12px; border: 1.5px solid #E8E8E8;
    background: #F7F7F7; display: flex; align-items: center; justify-content: center;
    cursor: pointer; font-size: 18px; transition: background .2s, transform .15s;
  }
  .maco-swap-btn:hover { background: #eee; transform: rotate(180deg); }

  .maco-search-btn {
    width: 100%; padding: 16px; border-radius: 14px; border: none; cursor: pointer;
    font-family: var(--font-cond); font-size: 18px; font-weight: 900; letter-spacing: 0.08em;
    text-transform: uppercase; color: #fff; background: var(--red);
    transition: background .2s, transform .15s; margin-top: 4px;
  }
  .maco-search-btn:hover { background: var(--red-dark); transform: translateY(-2px); }
  .maco-search-btn:active { transform: translateY(0); }
  .maco-search-footer { text-align: center; font-size: 11px; color: #aaa; margin-top: 14px; line-height: 1.5; }

  .maco-routes-section { background: #F4F4F4; padding: 64px 2rem; }
  .maco-routes-inner { max-width: 1280px; margin: 0 auto; }
  .maco-routes-header { display: flex; flex-direction: column; justify-content: space-between; gap: 8px; margin-bottom: 34px; }
  @media (min-width: 700px) { .maco-routes-header { flex-direction: row; align-items: flex-end; } }
  .maco-routes-eyebrow { font-family: var(--font-cond); font-size: 11px; font-weight: 900; letter-spacing: 0.18em; text-transform: uppercase; color: var(--red); margin-bottom: 6px; }
  .maco-routes-title { font-family: var(--font-cond); font-size: 42px; font-weight: 900; line-height: 1; color: #1C1C1E; }
  .maco-routes-sub { font-size: 14px; color: #888; margin-top: 6px; }
  .maco-routes-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

  .maco-bus-card {
    background: #fff; border-radius: 18px; padding: 22px;
    border: 1.5px solid #E8E8E8; cursor: pointer;
    transition: transform .22s, box-shadow .22s, border-color .22s;
    text-align: left;
  }
  .maco-bus-card:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(0,0,0,0.12); border-color: var(--red); }
  .maco-bus-card-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 18px; gap: 12px; }
  .maco-bus-card-route-label { font-size: 10px; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; color: #aaa; margin-bottom: 5px; }
  .maco-bus-card-route { font-family: var(--font-cond); font-size: 22px; font-weight: 900; color: #1C1C1E; display: flex; align-items: center; gap: 8px; }
  .maco-bus-card-arrow { color: var(--red); font-size: 18px; }
  .maco-bus-card-book-btn { flex-shrink: 0; font-family: var(--font-cond); font-size: 12px; font-weight: 900; letter-spacing: 0.1em; text-transform: uppercase; color: #fff; background: var(--red); border: none; border-radius: 7px; padding: 8px 14px; cursor: pointer; }
  .maco-bus-card-divider { height: 1px; background: #F0F0F0; margin-bottom: 16px; }
  .maco-bus-card-meta { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
  .maco-bus-card-meta-label { font-size: 9px; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; color: #bbb; margin-bottom: 4px; }
  .maco-bus-card-meta-val { font-family: var(--font-cond); font-size: 17px; font-weight: 900; color: #1C1C1E; }
  .maco-bus-card-meta-val.maco-price { color: var(--red); }
  .maco-bus-card-footer { margin-top: 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .maco-bus-card-cta { font-size: 13px; font-weight: 800; color: #bbb; transition: color .2s; }
  .maco-bus-card:hover .maco-bus-card-cta { color: var(--red); }
  .maco-bus-type-badge { font-size: 10px; font-weight: 900; letter-spacing: 0.06em; text-transform: uppercase; background: #F4F4F4; color: #888; border-radius: 5px; padding: 4px 8px; }

  .maco-empty { border: 2px dashed #DDD; border-radius: 18px; padding: 56px 32px; text-align: center; grid-column: 1/-1; }
  .maco-empty-icon { font-size: 40px; margin-bottom: 12px; }
  .maco-empty-title { font-family: var(--font-cond); font-size: 24px; font-weight: 900; color: #333; }
  .maco-empty-sub { font-size: 14px; color: #aaa; margin-top: 6px; }
  .maco-skeleton { border-radius: 18px; height: 180px; background: linear-gradient(90deg, #EBEBEB 25%, #F5F5F5 50%, #EBEBEB 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; }
  @keyframes shimmer { to { background-position: -200% 0; } }
  .maco-loading { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: var(--charcoal); gap: 16px; }
  .maco-loading-logo { width: 56px; height: 56px; border-radius: 14px; background: var(--red); display: flex; align-items: center; justify-content: center; font-family: var(--font-cond); font-size: 28px; font-weight: 900; color: #fff; animation: breathe 1.6s ease-in-out infinite; }
  @keyframes breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
  .maco-loading-text { font-size: 14px; color: var(--muted); }
  .maco-footer { background: var(--charcoal); border-top: 1px solid var(--border); padding: 22px 2rem; display: flex; align-items: center; justify-content: center; }
  .maco-footer-text { font-size: 12px; color: rgba(255,255,255,0.32); letter-spacing: 0.04em; }
  .maco-footer-text span { color: var(--red); }
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const el = document.createElement("style");
  el.textContent = STYLES;
  document.head.appendChild(el);
}

export default function OperatorLanding() {
  useEffect(() => {
    injectStyles();
  }, []);

  const navigate = useNavigate();
  const { operatorSlug } = useParams();

  const [brand, setBrand] = useState(null);
  const [loadingBrand, setLoadingBrand] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState(toLocalYYYYMMDD(new Date()));

  const themeColor = brand?.themeColor || "#E8192C";
  const logoUrl = normalizeLogoUrl(brand?.logoUrl);

  useEffect(() => {
    if (!themeColor) return;
    document.documentElement.style.setProperty("--red", themeColor);
  }, [themeColor]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingBrand(true);
        setRoutesLoading(true);

        const brandRes = await apiClient.get(`/operator-brands/${operatorSlug}`);
        setBrand(brandRes.data);

        const busRes = await apiClient.get(`/buses`, {
          params: { operatorKey: operatorSlug },
        });

        const items = Array.isArray(busRes.data) ? busRes.data : [];
        setRoutes(items.slice(0, 8));
      } catch {
        setBrand({
          slug: operatorSlug,
          name: "MACO",
          headline: "Travel Fast. Travel Safe.",
          description: "Fast and easy online bus booking.",
          themeColor: "#E8192C",
        });
        setRoutes([]);
      } finally {
        setLoadingBrand(false);
        setRoutesLoading(false);
      }
    };

    if (operatorSlug) fetchData();
  }, [operatorSlug]);

  const ownerName = useMemo(
    () => brand?.name || operatorSlug?.replace(/-/g, " ").toUpperCase() || "MACO",
    [brand, operatorSlug]
  );

  const headline = brand?.headline || `Book ${ownerName} Tickets Online`;
  const description =
    brand?.description ||
    "Search available buses, select your seats, choose boarding and dropping points, and continue securely with Routesbook.";

  const uniqueRouteCount = useMemo(
    () => new Set(routes.map((r) => `${normalizeCity(r.from)}-${normalizeCity(r.to)}`)).size,
    [routes]
  );

  const cityOptions = useMemo(() => {
    const values = new Set();
    routes.forEach((route) => {
      if (normalizeCity(route.from)) values.add(normalizeCity(route.from));
      if (normalizeCity(route.to)) values.add(normalizeCity(route.to));
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [routes]);

  const featuredRoutes = useMemo(() => routes.slice(0, 4), [routes]);

  useEffect(() => {
    if (!routes.length) return;
    if (!from && routes[0]?.from) setFrom(normalizeCity(routes[0].from));
    if (!to && routes[0]?.to) setTo(normalizeCity(routes[0].to));
  }, [routes, from, to]);

  const swapFromTo = () => {
    setFrom(to);
    setTo(from);
  };

  const goToBooking = (route) => {
    if (!route?.from || !route?.to) return;
    navigate(
      `/operator-booking/${operatorSlug}?from=${encodeURIComponent(
        normalizeCity(route.from)
      )}&to=${encodeURIComponent(normalizeCity(route.to))}&date=${date}&operatorKey=${operatorSlug}`
    );
  };

  const handleSearch = () => {
    if (!from || !to || !date) {
      alert("Please select From, To and Date.");
      return;
    }
    navigate(
      `/operator-booking/${operatorSlug}?from=${encodeURIComponent(
        from
      )}&to=${encodeURIComponent(to)}&date=${date}&operatorKey=${operatorSlug}`
    );
  };

  const renderLogo = () => {
    if (logoUrl) {
      return (
        <div className="maco-nav-logo-wrap">
          <img src={logoUrl} alt={ownerName} />
        </div>
      );
    }
    return <div className="maco-nav-logo-fallback">{getInitials(ownerName)}</div>;
  };

  if (loadingBrand) {
    return (
      <div className="maco-root">
        <div className="maco-loading">
          <div className="maco-loading-logo">{getInitials(ownerName)}</div>
          <p className="maco-loading-text">Loading booking page…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="maco-root">
      <div className="maco-page-bg">
        <div className="maco-brand-strip">
          <div className="maco-brand-left">
            {renderLogo()}
            <div>
              <div className="maco-nav-name">{ownerName}</div>
              <div className="maco-nav-sub">Official booking page</div>
            </div>
          </div>
          <div className="maco-strip-actions">
            <a href="#routes" className="maco-strip-link">
              Routes
            </a>
            <a href="#booking" className="maco-strip-cta">
              Book Now
            </a>
          </div>
        </div>

        <div className="maco-hero">
          <div className="maco-hero-content">
            <div className="maco-hero-eyebrow">
              <span className="maco-hero-eyebrow-dot" />
              Online Bus Booking
            </div>

            <h2 className="maco-hero-title">
              {headline.split(" ").length > 3 ? (
                <>
                  {headline
                    .split(" ")
                    .slice(0, Math.ceil(headline.split(" ").length / 2))
                    .join(" ")}
                  <br />
                  <span className="maco-hero-title-red">
                    {headline
                      .split(" ")
                      .slice(Math.ceil(headline.split(" ").length / 2))
                      .join(" ")}
                  </span>
                </>
              ) : (
                <>{headline}</>
              )}
            </h2>

            <p className="maco-hero-desc">{description}</p>

            <div className="maco-stats">
              <div className="maco-stat">
                <div className="maco-stat-num">{routes.length}</div>
                <div className="maco-stat-label">Buses</div>
              </div>
              <div className="maco-stat">
                <div className="maco-stat-num">{uniqueRouteCount}</div>
                <div className="maco-stat-label">Routes</div>
              </div>
              <div className="maco-stat">
                <div className="maco-stat-num">24/7</div>
                <div className="maco-stat-label">Access</div>
              </div>
            </div>

            <div className="maco-pills">
              <span className="maco-pill">Live seat availability</span>
              <span className="maco-pill">Secure payments</span>
              <span className="maco-pill">Instant tickets</span>
            </div>

            <div className="maco-quick-routes">
              <div className="maco-quick-routes-title">
                <h3>Available routes</h3>
                <span>{routes.length} buses linked</span>
              </div>

              <div className="maco-quick-grid">
                {routesLoading ? (
                  [1, 2, 3, 4].map((i) => <div key={i} className="maco-skeleton" />)
                ) : featuredRoutes.length ? (
                  featuredRoutes.map((route) => (
                    <button
                      key={`${route._id}-${route.departureTime}-hero`}
                      type="button"
                      className="maco-route-chip"
                      onClick={() => goToBooking(route)}
                    >
                      <div className="maco-route-main">
                        <div className="maco-route-name">
                          {route.from}
                          <span className="maco-route-arrow">→</span>
                          {route.to}
                        </div>
                        <span className="maco-route-book">Book</span>
                      </div>
                      <div className="maco-route-meta">
                        <span>{route.departureTime || "Time TBA"}</span>
                        <span className="maco-route-price">Rs. {route.price || 0}</span>
                      </div>
                    </button>
                  ))
                ) : (
                  <button type="button" className="maco-route-chip">
                    <div className="maco-route-main">
                      <div className="maco-route-name">No buses linked yet</div>
                    </div>
                    <div className="maco-route-meta">
                      <span>Add matching WhatsApp number to buses.</span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div id="booking" className="maco-hero-search">
            <div className="maco-search-card">
              <div className="maco-search-header">
                <div>
                  <div className="maco-search-badge">{ownerName}</div>
                  <div className="maco-search-title">
                    Find your
                    <br />
                    journey
                  </div>
                  <div className="maco-search-sub">Showing {ownerName} buses only</div>
                </div>
                {logoUrl && <img src={logoUrl} alt={ownerName} className="maco-search-logo" />}
              </div>

              <div className="maco-swap-row">
                <div>
                  <label className="maco-label">From</label>
                  <select
                    className="maco-input"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  >
                    <option value="">Select city</option>
                    {cityOptions.map((city) => (
                      <option key={`from-${city}`} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <button className="maco-swap-btn" onClick={swapFromTo} title="Swap" type="button">
                    ⇄
                  </button>
                </div>
                <div>
                  <label className="maco-label">To</label>
                  <select className="maco-input" value={to} onChange={(e) => setTo(e.target.value)}>
                    <option value="">Select city</option>
                    {cityOptions.map((city) => (
                      <option key={`to-${city}`} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="maco-field">
                <label className="maco-label">Travel Date</label>
                <input
                  type="date"
                  className="maco-input"
                  value={date}
                  min={toLocalYYYYMMDD(new Date())}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <button className="maco-search-btn" onClick={handleSearch} type="button">
                Search Available Buses →
              </button>

              <p className="maco-search-footer">
                Seat selection, boarding points &amp; payment continue securely on Routesbook.
              </p>
            </div>
          </div>
        </div>
      </div>

      <section id="routes" className="maco-routes-section">
        <div className="maco-routes-inner">
          <div className="maco-routes-header">
            <div>
              <p className="maco-routes-eyebrow">Direct booking links</p>
              <h3 className="maco-routes-title">Available {ownerName} Buses</h3>
              <p className="maco-routes-sub">Tap a route to open the secure booking flow.</p>
            </div>
          </div>

          <div className="maco-routes-grid">
            {routesLoading ? (
              [1, 2, 3].map((i) => <div key={i} className="maco-skeleton" />)
            ) : routes.length ? (
              routes.map((route) => (
                <button
                  key={`${route._id}-${route.departureTime}`}
                  className="maco-bus-card"
                  onClick={() => goToBooking(route)}
                  type="button"
                >
                  <div className="maco-bus-card-top">
                    <div>
                      <div className="maco-bus-card-route-label">Route</div>
                      <div className="maco-bus-card-route">
                        {route.from}
                        <span className="maco-bus-card-arrow">→</span>
                        {route.to}
                      </div>
                    </div>
                    <span className="maco-bus-card-book-btn">Book</span>
                  </div>

                  <div className="maco-bus-card-divider" />

                  <div className="maco-bus-card-meta">
                    <div className="maco-bus-card-meta-item">
                      <div className="maco-bus-card-meta-label">Departure</div>
                      <div className="maco-bus-card-meta-val">{route.departureTime || "—"}</div>
                    </div>
                    <div className="maco-bus-card-meta-item">
                      <div className="maco-bus-card-meta-label">Type</div>
                      <div className="maco-bus-card-meta-val">{route.busType || "Bus"}</div>
                    </div>
                    <div className="maco-bus-card-meta-item">
                      <div className="maco-bus-card-meta-label">Fare</div>
                      <div className="maco-bus-card-meta-val maco-price">Rs. {route.price || 0}</div>
                    </div>
                  </div>

                  <div className="maco-bus-card-footer">
                    <span className="maco-bus-card-cta">Select seats &amp; boarding →</span>
                    <span className="maco-bus-type-badge">{route.busType || "Standard"}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="maco-empty">
                <div className="maco-empty-icon">🚌</div>
                <div className="maco-empty-title">No buses linked yet</div>
                <div className="maco-empty-sub">
                  Add the same WhatsApp number to buses and the operator brand to show direct route
                  cards here.
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="maco-footer">
        <p className="maco-footer-text">
          Powered by <span>Routesbook</span> · Secure online bus booking
        </p>
      </footer>
    </div>
  );
}
